import React from 'react';
import { motion } from 'framer-motion';
import { useDarkMode } from '../../../context/DarkModeContext';
import { Recommendation } from '../../../utils/reviewPrepCalculations';
import { 
  LightBulbIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { useEvidenceConfig } from '../../../hooks/useEvidenceConfig';

interface SmartRecommendationsProps {
  recommendations: Recommendation[];
  onActionClick: (recommendation: Recommendation) => void;
  focusId?: string;
}

const SmartRecommendations: React.FC<SmartRecommendationsProps> = ({ 
  recommendations, 
  onActionClick,
  focusId 
}) => {
  const { darkMode } = useDarkMode();
  const { config } = useEvidenceConfig(focusId || '');
  
  const bgColor = darkMode ? 'bg-gray-800' : 'bg-white';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  
  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'high':
        return <ExclamationCircleIcon className="w-5 h-5 text-red-500" />;
      case 'medium':
        return <InformationCircleIcon className="w-5 h-5 text-yellow-500" />;
      case 'low':
        return <InformationCircleIcon className="w-5 h-5 text-blue-500" />;
      default:
        return null;
    }
  };
  
  const getActionButtonLabel = (actionType: string) => {
    switch (actionType) {
      case 'add_evidence':
        return 'Add Evidence';
      case 'update_evidence':
        return 'Update Evidence';
      case 'review':
        return 'Take Action';
      default:
        return 'Action';
    }
  };
  
  const getRecommendationBg = (priority: string) => {
    if (!darkMode) {
      switch (priority) {
        case 'high':
          return 'bg-red-50 border-red-200';
        case 'medium':
          return 'bg-yellow-50 border-yellow-200';
        case 'low':
          return 'bg-blue-50 border-blue-200';
        default:
          return 'bg-gray-50 border-gray-200';
      }
    } else {
      switch (priority) {
        case 'high':
          return 'bg-red-900/20 border-red-800';
        case 'medium':
          return 'bg-yellow-900/20 border-yellow-800';
        case 'low':
          return 'bg-blue-900/20 border-blue-800';
        default:
          return 'bg-gray-800 border-gray-700';
      }
    }
  };
  
  if (recommendations.length === 0) {
    return null;
  }
  
  // Use configurable limit for recommendations
  const limit = config.displayLimits?.recommendations_count || 3;
  const topRecommendations = recommendations.slice(0, limit);
  
  // Group recommendations by priority
  const highPriority = topRecommendations.filter(r => r.priority === 'high');
  const mediumPriority = topRecommendations.filter(r => r.priority === 'medium');
  const lowPriority = topRecommendations.filter(r => r.priority === 'low');
  
  return (
    <div className={`${bgColor} rounded-lg border ${borderColor} p-6`}>
      <div className="flex items-center gap-2 mb-4">
        <LightBulbIcon className="w-6 h-6 text-yellow-500" />
        <h3 className={`text-lg font-semibold ${textColor}`}>
          Smart Recommendations
        </h3>
      </div>
      
      <p className={`text-sm ${mutedTextColor} mb-6`}>
        Based on your progress:
      </p>
      
      <div className="space-y-3">
        {/* High Priority */}
        {highPriority.length > 0 && (
          <div className="space-y-2">
            {highPriority.map((rec, index) => (
              <motion.div
                key={`high-${index}`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`p-4 rounded-lg border ${getRecommendationBg(rec.priority)} transition-all`}
              >
                <div className="flex items-start gap-3">
                  {getPriorityIcon(rec.priority)}
                  <div className="flex-1">
                    <p className={`text-sm ${textColor}`}>{rec.message}</p>
                    {rec.category !== 'general' && (
                      <p className={`text-xs ${mutedTextColor} mt-1`}>
                        Category: {rec.category}
                      </p>
                    )}
                  </div>
                  {rec.actionType !== 'review' && (
                    <button
                      type="button"
                      onClick={() => onActionClick(rec)}
                      className={`px-3 py-1 text-xs rounded-md transition-colors flex items-center gap-1 ${
                        darkMode
                          ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                          : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-300'
                      }`}
                    >
                      {getActionButtonLabel(rec.actionType)}
                      <ArrowRightIcon className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
        
        {/* Medium Priority */}
        {mediumPriority.length > 0 && (
          <div className="space-y-2">
            {mediumPriority.map((rec, index) => (
              <motion.div
                key={`medium-${index}`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: (highPriority.length + index) * 0.1 }}
                className={`p-4 rounded-lg border ${getRecommendationBg(rec.priority)} transition-all`}
              >
                <div className="flex items-start gap-3">
                  {getPriorityIcon(rec.priority)}
                  <div className="flex-1">
                    <p className={`text-sm ${textColor}`}>{rec.message}</p>
                    {rec.category !== 'general' && (
                      <p className={`text-xs ${mutedTextColor} mt-1`}>
                        Category: {rec.category}
                      </p>
                    )}
                  </div>
                  {rec.actionType !== 'review' && (
                    <button
                      type="button"
                      onClick={() => onActionClick(rec)}
                      className={`px-3 py-1 text-xs rounded-md transition-colors flex items-center gap-1 ${
                        darkMode
                          ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                          : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-300'
                      }`}
                    >
                      {getActionButtonLabel(rec.actionType)}
                      <ArrowRightIcon className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
        
        {/* Low Priority */}
        {lowPriority.length > 0 && (
          <div className="space-y-2">
            {lowPriority.map((rec, index) => (
              <motion.div
                key={`low-${index}`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: (highPriority.length + mediumPriority.length + index) * 0.1 }}
                className={`p-4 rounded-lg border ${getRecommendationBg(rec.priority)} transition-all`}
              >
                <div className="flex items-start gap-3">
                  {getPriorityIcon(rec.priority)}
                  <div className="flex-1">
                    <p className={`text-sm ${textColor}`}>{rec.message}</p>
                    {rec.category !== 'general' && (
                      <p className={`text-xs ${mutedTextColor} mt-1`}>
                        Category: {rec.category}
                      </p>
                    )}
                  </div>
                  {rec.actionType !== 'review' && (
                    <button
                      type="button"
                      onClick={() => onActionClick(rec)}
                      className={`px-3 py-1 text-xs rounded-md transition-colors flex items-center gap-1 ${
                        darkMode
                          ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                          : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-300'
                      }`}
                    >
                      {getActionButtonLabel(rec.actionType)}
                      <ArrowRightIcon className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
      
    </div>
  );
};

export default SmartRecommendations;